// Hook do catalogo de midia — banners, folders, comparativos.
// 1 imagem = 1 oferta (pode conter varios planos). Consultor manda via
// botao no Inbox WhatsApp; IA manda via tool enviar_item_catalogo.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface CatalogoItem {
  id: string;
  titulo: string;
  descricao: string | null;
  imagem_url: string;
  tags: string[];
  categoria: string | null;
  ativo: boolean;
  ordem: number;
  created_at: string;
  updated_at: string;
}

export const CATALOGO_QUERY_KEY = ["catalogo_midia"] as const;

export function useCatalogoMidia() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: CATALOGO_QUERY_KEY,
    queryFn: async (): Promise<CatalogoItem[]> => {
      const { data, error } = await supabase
        .from("catalogo_midia" as any)
        .select("*")
        .order("ordem", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CatalogoItem[];
    },
  });

  const create = useMutation({
    mutationFn: async (input: Omit<CatalogoItem, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("catalogo_midia" as any)
        .insert([input as any])
        .select()
        .single();
      if (error) throw error;
      return data as CatalogoItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CATALOGO_QUERY_KEY });
      toast({ title: "Item criado no catálogo" });
    },
    onError: (err: any) => toast({ title: "Erro ao criar", description: err?.message, variant: "destructive" }),
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; patch: Partial<CatalogoItem> }) => {
      const { error } = await supabase
        .from("catalogo_midia" as any)
        .update(input.patch as any)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CATALOGO_QUERY_KEY });
      toast({ title: "Item atualizado" });
    },
    onError: (err: any) => toast({ title: "Erro ao salvar", description: err?.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("catalogo_midia" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CATALOGO_QUERY_KEY });
      toast({ title: "Item removido" });
    },
  });

  return { ...query, items: query.data ?? [], create, update, remove };
}
